/**
 * Chord string parsing: display string → V3 chord model.
 */
import { normalizeAccidentalSymbol } from './enharmonicUtils.js';
import { parseNote } from './noteMigration.js';
import alterationsDataFromFile from '../data/chord-alterations.json' with { type: 'json' };
// Prepare alterationsData for use in parseChordString, adding oldRegex
const alterationsData = alterationsDataFromFile.map((alt) => {
    // Create a regex to match old ASCII style alterations, e.g., "b5", "#11"
    // Ensure it matches at the end of a string segment.
    const symbol = alt.value.startsWith('♭') || alt.value.startsWith('b') ? 'b' : '#';
    const number = alt.value.match(/\d+/)?.[0];
    const oldRegex = new RegExp(`${symbol}${number}$`);
    return { ...alt, oldRegex };
});
/**
 * Strips known alterations (♭5, ♯9, … or ASCII b5, #9, …) from the end of a
 * chord-string segment. Returns the consumed alterations in left-to-right
 * order along with the unconsumed remainder.
 */
const extractTrailingAlterations = (segment) => {
    const alterations = [];
    let rest = segment;
    let changedInIteration = true;
    while (changedInIteration) {
        changedInIteration = false;
        // Try matching with new symbols first
        for (const altDefinition of alterationsData) {
            if (rest.endsWith(altDefinition.value)) { // e.g., endsWith("♭5")
                alterations.unshift(altDefinition.value);
                rest = rest.substring(0, rest.length - altDefinition.value.length);
                changedInIteration = true;
                break;
            }
        }
        if (changedInIteration)
            continue; // Found one, restart loop
        // If not found, try matching with old symbols (e.g., b5, #5)
        for (const altDefinition of alterationsData) {
            if (altDefinition.oldRegex.test(rest)) {
                const match = rest.match(altDefinition.oldRegex);
                if (match && match[0]) {
                    alterations.unshift(altDefinition.value); // Store the normalized value
                    rest = rest.substring(0, rest.length - match[0].length);
                    changedInIteration = true;
                    break;
                }
            }
        }
    }
    return { rest, alterations };
};
/** A "6/9" or "69" before any bass slash ("C6/9", "Cm69", "C6/9/E"; not "C6/9" in "C6/91"). */
const SIX_NINE = /^([^/]*?)6\/?9(?!\d)/;
/**
 * The grammar shared by parseChordString and parseChordStringStrict.
 *
 * `unparsed` collects the text the grammar could not account for: an invalid
 * or lowercase bass note, and whatever is left after the root, alterations,
 * adds, quality and extension have been consumed. The lenient parser ignores
 * it (graceful degradation for user input); the strict reader rejects any
 * string that leaves some.
 */
const parseChordStringWithRemainder = (chordString) => {
    const result = {
        root: null, quality: null, extension: null,
        isDominant: false, adds: [], alterations: [], bass: null,
    };
    if (!chordString || chordString.trim() === "" || chordString === "- - -")
        return { chord: result, unparsed: "" };
    // "6/9" and "69" mean a sixth chord with an added ninth. Rewrite them to the
    // "6add9" the grammar reads before the bass split, which would otherwise take
    // the "/9" for a bass note ("D6/9" → D6, "D6/9/F♯" → D6/F♯ without the 9th).
    let remainingChord = chordString.trim().replace(SIX_NINE, "$16add9");
    let unparsedBass = "";
    // Step 1: Extract bass note (from the end, after /)
    const bassParts = remainingChord.split('/');
    if (bassParts.length > 1) {
        const potentialBass = bassParts.pop();
        if (potentialBass) {
            // Try to parse bass note, but handle errors gracefully
            try {
                result.bass = parseNote(normalizeAccidentalSymbol(potentialBass));
                // parseNote accepts "e" as E; a strict reading only takes "C/E"
                if (potentialBass[0] !== potentialBass[0].toUpperCase())
                    unparsedBass = `/${potentialBass}`;
            }
            catch (error) {
                // Invalid bass note, set to null (graceful degradation)
                result.bass = null;
                unparsedBass = `/${potentialBass}`;
            }
            remainingChord = bassParts.join('/');
        }
    }
    // Step 2: Extract root note (from the beginning)
    // This MUST be done before parsing alterations to avoid confusing F♯9 with F + ♯9 alteration
    const canonicalNotes = ["C", "C♯", "D♭", "D", "D♯", "E♭", "E", "F", "F♯", "G♭", "G", "G♯", "A♭", "A", "A♯", "B♭", "B", "F♭", "E♯", "C♭", "B♯"];
    let rootFound = false;
    // Try to match 2-character root (e.g., C#, Db, C♯, D♭)
    if (remainingChord.length >= 2) {
        const twoCharCandidateRaw = remainingChord.substring(0, 2);
        const twoCharCandidateNormalized = normalizeAccidentalSymbol(twoCharCandidateRaw);
        if (canonicalNotes.includes(twoCharCandidateNormalized)) {
            // Try to parse root note, but handle errors gracefully
            try {
                result.root = parseNote(twoCharCandidateNormalized);
                remainingChord = remainingChord.substring(twoCharCandidateRaw.length); // Use raw length for slicing
                rootFound = true;
            }
            catch (error) {
                // Invalid root, continue to try 1-character
            }
        }
    }
    // If not found, try to match 1-character root (e.g., C, D, E)
    if (!rootFound && remainingChord.length >= 1) {
        const oneCharCandidateRaw = remainingChord.substring(0, 1);
        // No normalization needed for single natural notes, but check against canonical.
        if (canonicalNotes.includes(oneCharCandidateRaw)) {
            // Try to parse root note, but handle errors gracefully
            try {
                result.root = parseNote(oneCharCandidateRaw);
                remainingChord = remainingChord.substring(oneCharCandidateRaw.length);
                rootFound = true;
            }
            catch (error) {
                // Invalid root, return early with null
            }
        }
    }
    // If no root found, return early
    if (!rootFound) {
        return { chord: result, unparsed: remainingChord + unparsedBass };
    }
    // Step 3: Extract alterations (from the end)
    // Now it's safe to parse alterations since we've already extracted the root
    if (remainingChord.endsWith("alt")) {
        result.alterations.push("alt");
        remainingChord = remainingChord.substring(0, remainingChord.length - 3);
    }
    else {
        // Alterations may be parenthesized (e.g., "C(♭5)", "C7(♭9)"). The builder
        // parenthesizes alterations that directly follow a bare root, which would
        // otherwise merge with the root or read as an extension.
        const parenMatch = remainingChord.match(/^(.*)\(([^()]+)\)$/);
        if (parenMatch) {
            const { rest, alterations } = extractTrailingAlterations(parenMatch[2]);
            if (rest === "" && alterations.length > 0) {
                result.alterations.push(...alterations);
                remainingChord = parenMatch[1];
            }
        }
        if (result.alterations.length === 0) {
            const { rest, alterations } = extractTrailingAlterations(remainingChord);
            result.alterations.push(...alterations);
            remainingChord = rest;
        }
    }
    // Step 4: Extract adds (from the end)
    const validAddStrings = ["add2", "add4", "add6", "add9", "add11", "add13"];
    let changedInAddsIteration = true;
    while (changedInAddsIteration) {
        changedInAddsIteration = false;
        for (const add of validAddStrings) {
            if (remainingChord.endsWith(add)) {
                result.adds.unshift(add);
                remainingChord = remainingChord.substring(0, remainingChord.length - add.length);
                changedInAddsIteration = true;
                break;
            }
        }
    }
    // Step 5: Parse quality and extension from what remains
    // Note: V3 uses "major" instead of "" for major quality, and null instead of "-" for no extension
    if (remainingChord.length > 0) {
        const combinedPatterns = [
            // New patterns with symbols first for precedence
            { match: "°7", quality: "diminished", extension: "7", isDominant: false },
            { match: "+maj7", quality: "augmented", extension: "maj7", isDominant: false }, // C+maj7 -> Caug(maj7)
            { match: "+9", quality: "augmented", extension: "9", isDominant: false }, // C+9 -> Caug9
            { match: "+7", quality: "augmented", extension: "7", isDominant: false }, // C+7 -> Caug7
            // Patterns for CM7, CM9 etc. (uppercase M)
            { match: "M13", quality: "major", extension: "13", isDominant: false },
            { match: "M11", quality: "major", extension: "11", isDominant: false },
            { match: "M9", quality: "major", extension: "9", isDominant: false },
            { match: "M7", quality: "major", extension: "7", isDominant: false },
            { match: "M6", quality: "major", extension: "6", isDominant: false },
            // Minor-Major chords
            { match: "mM13", quality: "minorMajor", extension: "13", isDominant: false },
            { match: "mM11", quality: "minorMajor", extension: "11", isDominant: false },
            { match: "mM9", quality: "minorMajor", extension: "9", isDominant: false },
            { match: "mM7", quality: "minorMajor", extension: "7", isDominant: false },
            // Existing patterns (maj, min, m)
            { match: "maj13", quality: "major", extension: "13", isDominant: false },
            { match: "maj9", quality: "major", extension: "9", isDominant: false },
            { match: "maj7", quality: "major", extension: "7", isDominant: false },
            { match: "min13", quality: "minor", extension: "13", isDominant: false }, { match: "m13", quality: "minor", extension: "13", isDominant: false },
            { match: "min11", quality: "minor", extension: "11", isDominant: false }, { match: "m11", quality: "minor", extension: "11", isDominant: false },
            { match: "min9", quality: "minor", extension: "9", isDominant: false }, { match: "m9", quality: "minor", extension: "9", isDominant: false },
            { match: "min7", quality: "minor", extension: "7", isDominant: false }, { match: "m7", quality: "minor", extension: "7", isDominant: false },
            { match: "min6", quality: "minor", extension: "6", isDominant: false }, { match: "m6", quality: "minor", extension: "6", isDominant: false },
            { match: "dim9", quality: "diminished", extension: "9", isDominant: false },
            { match: "dim7", quality: "diminished", extension: "7", isDominant: false },
            { match: "dim6", quality: "diminished", extension: "6", isDominant: false },
            { match: "aug9", quality: "augmented", extension: "9", isDominant: false },
            { match: "aug7", quality: "augmented", extension: "7", isDominant: false },
            { match: "aug6", quality: "augmented", extension: "6", isDominant: false },
            { match: "7sus4", quality: "sus4", extension: "7", isDominant: true },
            // Power chord (C5, G5) — must come before bare dominant number extensions
            { match: "5", quality: "power", extension: null, isDominant: false },
            { match: "13", quality: "major", extension: "13", isDominant: true }, { match: "11", quality: "major", extension: "11", isDominant: true },
            { match: "9", quality: "major", extension: "9", isDominant: true }, { match: "7", quality: "major", extension: "7", isDominant: true },
            { match: "6", quality: "major", extension: "6", isDominant: false },
        ];
        let coreParsed = false;
        for (const p of combinedPatterns) {
            if (remainingChord.startsWith(p.match)) {
                result.quality = p.quality;
                result.extension = p.extension;
                result.isDominant = p.isDominant !== undefined ? p.isDominant : false;
                remainingChord = remainingChord.substring(p.match.length);
                coreParsed = true;
                break;
            }
        }
        if (coreParsed && remainingChord.length > 0 && result.quality === "major" && result.isDominant) {
            const qualityOnlyPatternsForDominantSus = [
                { match: "sus4", quality: "sus4" }, { match: "sus2", quality: "sus2" },
            ];
            for (const qp of qualityOnlyPatternsForDominantSus) {
                if (remainingChord.startsWith(qp.match)) {
                    result.quality = qp.quality;
                    remainingChord = remainingChord.substring(qp.match.length);
                    break;
                }
            }
        }
        if (!coreParsed || (coreParsed && result.extension === null && remainingChord.length > 0)) {
            const qualityOnlyPatterns = [
                // New symbol patterns first
                { match: "°", quality: "diminished" },
                { match: "+", quality: "augmented" },
                // Existing patterns
                { match: "diminished", quality: "diminished" }, { match: "augmented", quality: "augmented" },
                { match: "sus4", quality: "sus4" }, { match: "sus2", quality: "sus2" },
                { match: "major", quality: "major" }, { match: "minor", quality: "minor" },
                { match: "maj", quality: "major" }, { match: "min", quality: "minor" },
                { match: "dim", quality: "diminished" }, { match: "aug", quality: "augmented" },
                { match: "m", quality: "minor" },
            ];
            let qualityExplicitlyParsedInB = false;
            if (!coreParsed || result.quality === null) {
                for (const qp of qualityOnlyPatterns) {
                    if (remainingChord.startsWith(qp.match)) {
                        result.quality = qp.quality;
                        remainingChord = remainingChord.substring(qp.match.length);
                        result.isDominant = false;
                        qualityExplicitlyParsedInB = true;
                        break;
                    }
                }
            }
            if (result.extension === null && remainingChord.length > 0) {
                const extensionOnlyPatterns = [
                    { match: "13", extension: "13" }, { match: "11", extension: "11" },
                    { match: "9", extension: "9" }, { match: "7", extension: "7" },
                    { match: "6", extension: "6" },
                ];
                for (const ep of extensionOnlyPatterns) {
                    if (remainingChord.startsWith(ep.match)) {
                        result.extension = ep.extension;
                        remainingChord = remainingChord.substring(ep.match.length);
                        if (!qualityExplicitlyParsedInB && result.quality === null) {
                            if (["7", "9", "11", "13"].includes(result.extension)) {
                                result.isDominant = true;
                                result.quality = "major";
                            }
                            else if (result.extension === "6") {
                                result.quality = "major";
                                result.isDominant = false;
                            }
                        }
                        else if (result.quality !== null && result.quality !== "major" && result.quality !== "minor" && result.quality !== "diminished" && result.quality !== "augmented" && result.quality !== "sus4" && result.quality !== "sus2") {
                            result.isDominant = false;
                        }
                        break;
                    }
                }
            }
        }
        if (result.root && result.quality === null && remainingChord === "") {
            result.quality = "major";
            result.isDominant = false;
            result.extension = null;
        }
    }
    else if (result.root && remainingChord === "") {
        result.quality = "major";
        result.isDominant = false;
        result.extension = null;
    }
    return { chord: result, unparsed: remainingChord + unparsedBass };
};
/**
 * Parse a chord string into a V3 chord model.
 *
 * Returns V3 format with Note objects for root and bass.
 * Handles both ASCII and Unicode accidentals gracefully.
 * Lenient: text the grammar does not recognize is ignored ("Cxyz" → C).
 *
 * @param chordString - Chord string to parse (e.g., "Cmaj7", "F♯m/C♯")
 * @returns V3 format chord model
 */
export const parseChordString = (chordString) => parseChordStringWithRemainder(chordString).chord;
/**
 * Parse a chord string only if the whole string is a chord.
 *
 * Same grammar as parseChordString, but returns null when any part of the
 * string is left unrecognized ("Be", "Cxyz", "C/H", "C/e") or there is no
 * root. Use it to decide whether a token of free text is a chord at all.
 *
 * @param chordString - Candidate chord string (e.g., "Am7", "F#m/C#")
 * @returns V3 chord model, or null if the string is not entirely a chord
 */
export const parseChordStringStrict = (chordString) => {
    const { chord, unparsed } = parseChordStringWithRemainder(chordString);
    return chord.root && unparsed === "" ? chord : null;
};
